import React, { useState, useEffect, useRef } from 'react';
import { FieldRow, InputField } from './shared/FormComponents';

const RoomProductModal = ({ isOpen, onClose, onSave, onDelete, initialData = null, roomTypeName = '' }) => {
  const [formData, setFormData] = useState(initialData || {
    상품명: '',
    확정방식: '자동확정',
    환불가능여부: '환불가능',
    포함서비스: []
  });

  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropMouseDownRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // 포함서비스가 없거나 빈 배열이면 기본값 설정
        const dataToSet = {
          ...initialData,
          포함서비스: Array.isArray(initialData.포함서비스) ? initialData.포함서비스 : []
        };
        setFormData(dataToSet);
      } else {
        // 새 상품 추가 시 기본값
        setFormData({
          상품명: '',
          확정방식: '자동확정',
          환불가능여부: '환불가능',
          포함서비스: []
        });
      }
      const id = requestAnimationFrame(() => setMountedVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setMountedVisible(false);
    }
  }, [isOpen, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const requestClose = () => {
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      onClose && onClose();
      setAnimatingOut(false);
    }, 220);
  };

  const handleSave = () => {
    // 유효성 검사
    if (!formData.상품명?.trim()) {
      alert('상품명을 입력해주세요.');
      return;
    }
    
    onSave && onSave(formData);
    requestClose();
  };

  if (!isOpen) return null;

  const iconOptions = [
    { value: '', label: '없음' },
    { value: '✓', label: '✓ 체크' },
    { value: '○', label: '○ 원' },
    { value: '🍳', label: '🍳 조식' },
    { value: '🎨', label: '🎨 미술관' },
    { value: '🎫', label: '🎫 티켓' },
    { value: '🚗', label: '🚗 차량' },
    { value: '🏊', label: '🏊 수영장' },
    { value: '💆', label: '💆 스파' },
    { value: '🎁', label: '🎁 선물' },
    { value: '⭐', label: '⭐ 스페셜' },
  ];

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 transition-opacity duration-200 ${
        animatingOut ? 'ease-in opacity-0' : 'ease-out'
      } ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
      onMouseDown={(e) => {
        backdropMouseDownRef.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        if (backdropMouseDownRef.current && e.target === e.currentTarget) {
          requestClose();
        }
        backdropMouseDownRef.current = false;
      }}
    >
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white shadow-2xl rounded-2xl z-50 max-h-[90vh] overflow-y-auto transform transition-all duration-200 ${
          animatingOut ? 'ease-in scale-95 opacity-0' : 'ease-out'
        } ${mountedVisible && !animatingOut ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? '상품 수정' : '상품 추가'}
              {roomTypeName && <span className="ml-2 text-sm text-gray-500">({roomTypeName})</span>}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* 기본 정보 */}
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-3 px-2 py-2 bg-gray-100 rounded-md">
              기본 정보
            </h3>
            <div className="space-y-3">
              <FieldRow label="상품명" required>
                <InputField
                  name="상품명"
                  value={formData.상품명}
                  onChange={(e) => handleChange('상품명', e.target.value)}
                  placeholder="예: 조식 포함 패키지, 룸 온리"
                />
              </FieldRow>
              
              <FieldRow label="확정방식" required>
                <div className="px-3 py-2">
                  <select
                    value={formData.확정방식}
                    onChange={(e) => handleChange('확정방식', e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="자동확정">자동확정</option>
                    <option value="관리자확인후확정">관리자 확인 후 확정</option>
                  </select>
                </div>
              </FieldRow>

              <FieldRow label="환불가능 여부" required>
                <div className="px-3 py-2">
                  <select
                    value={formData.환불가능여부}
                    onChange={(e) => handleChange('환불가능여부', e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="환불가능">환불가능</option>
                    <option value="환불불가">환불불가</option>
                  </select>
                </div>
              </FieldRow>
            </div>
          </div>

          {/* 포함서비스 */}
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-3 px-2 py-2 bg-gray-100 rounded-md flex items-center justify-between">
              <span>포함서비스</span>
              <button
                type="button"
                onClick={() => {
                  const newService = {
                    서비스명: '',
                    무료성인수: 0,
                    무료아동수: 0,
                    성인원가: 0,
                    아동원가: 0,
                    성인판매가: 0,
                    아동판매가: 0
                  };
                  handleChange('포함서비스', [...formData.포함서비스, newService]);
                }}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                + 서비스 추가
              </button>
            </h3>
            
            <div className="space-y-4">
              {formData.포함서비스.map((service, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">서비스 #{index + 1}</h4>
                    {formData.포함서비스.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = formData.포함서비스.filter((_, i) => i !== index);
                          handleChange('포함서비스', updated);
                        }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">서비스명</label>
                      <input
                        type="text"
                        value={service.서비스명}
                        onChange={(e) => {
                          const updated = [...formData.포함서비스];
                          updated[index].서비스명 = e.target.value;
                          handleChange('포함서비스', updated);
                        }}
                        className="w-full px-3 py-2 border rounded text-sm"
                        placeholder="예: 조식 뷔페"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">무료 성인수</label>
                        <input
                          type="number"
                          min="0"
                          value={service.무료성인수}
                          onChange={(e) => {
                            const updated = [...formData.포함서비스];
                            updated[index].무료성인수 = Number(e.target.value);
                            handleChange('포함서비스', updated);
                          }}
                          className="w-full px-3 py-2 border rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">무료 아동수</label>
                        <input
                          type="number"
                          min="0"
                          value={service.무료아동수}
                          onChange={(e) => {
                            const updated = [...formData.포함서비스];
                            updated[index].무료아동수 = Number(e.target.value);
                            handleChange('포함서비스', updated);
                          }}
                          className="w-full px-3 py-2 border rounded text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">성인 원가</label>
                        <input
                          type="text"
                          value={service.성인원가?.toLocaleString() || '0'}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(value)) {
                              const updated = [...formData.포함서비스];
                              updated[index].성인원가 = Number(value) || 0;
                              handleChange('포함서비스', updated);
                            }
                          }}
                          className="w-full px-3 py-2 border rounded text-sm text-right"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">아동 원가</label>
                        <input
                          type="text"
                          value={service.아동원가?.toLocaleString() || '0'}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(value)) {
                              const updated = [...formData.포함서비스];
                              updated[index].아동원가 = Number(value) || 0;
                              handleChange('포함서비스', updated);
                            }
                          }}
                          className="w-full px-3 py-2 border rounded text-sm text-right"
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">성인 판매가</label>
                        <input
                          type="text"
                          value={service.성인판매가?.toLocaleString() || '0'}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(value)) {
                              const updated = [...formData.포함서비스];
                              updated[index].성인판매가 = Number(value) || 0;
                              handleChange('포함서비스', updated);
                            }
                          }}
                          className="w-full px-3 py-2 border rounded text-sm text-right"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">아동 판매가</label>
                        <input
                          type="text"
                          value={service.아동판매가?.toLocaleString() || '0'}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(value)) {
                              const updated = [...formData.포함서비스];
                              updated[index].아동판매가 = Number(value) || 0;
                              handleChange('포함서비스', updated);
                            }
                          }}
                          className="w-full px-3 py-2 border rounded text-sm text-right"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
          {/* 삭제 버튼 (편집 모드일 때만 표시) */}
          {initialData && onDelete ? (
            <button
              onClick={() => {
                if (window.confirm(`"${formData.상품명}" 상품을 삭제하시겠습니까?`)) {
                  onDelete();
                  requestClose();
                }
              }}
              className="px-5 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors font-medium"
            >
              삭제
            </button>
          ) : (
            <div></div>
          )}
          
          {/* 취소/저장 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={requestClose}
              className="px-5 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomProductModal;
